import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { X, Loader2, Brain, Sparkles } from "lucide-react";

interface Props {
  stressEvent: { id: string; detected_at: string; status: string };
  onClose: () => void;
  onComplete: () => void;
}

const baseQuestions = [
  {
    id: "q1",
    question: "O que você estava fazendo quando sentiu o pico de estresse?",
    options: ["Trabalhando", "Em reunião", "Cuidando de alguém", "Dirigindo", "Em casa relaxando", "Outro"],
  },
  {
    id: "q2",
    question: "Como você descreveria a intensidade do momento?",
    options: ["Leve — um desconforto", "Moderada — difícil de ignorar", "Intensa — muito angustiante", "Extrema — senti que perdi o controle"],
  },
  {
    id: "q3",
    question: "Você já sentiu algo parecido antes?",
    options: ["É a primeira vez", "Raramente", "Frequentemente", "É recorrente na minha rotina"],
  },
  {
    id: "q4",
    question: "Qual foi a sua reação imediata?",
    options: ["Tentei respirar fundo", "Chorei ou me emocionei", "Fiquei paralisado(a)", "Fiquei irritado(a)", "Não percebi na hora"],
  },
];

const StressQuestionnaire = ({ stressEvent, onClose, onComplete }: Props) => {
  const isAnswered = stressEvent.status === "answered";
  const [step, setStep] = useState<"base" | "open" | "ai" | "insights" | "loading">(isAnswered ? "loading" : "base");
  const [baseAnswers, setBaseAnswers] = useState<Record<string, string>>({});
  const [openAnswer, setOpenAnswer] = useState("");
  const [aiQuestions, setAiQuestions] = useState<Array<{ question: string; options: string[] }>>([]);
  const [aiAnswers, setAiAnswers] = useState<Record<string, string>>({});
  const [insights, setInsights] = useState<any>(null);
  const [savedResponse, setSavedResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Load existing response for answered events
  useEffect(() => {
    if (!isAnswered) return;
    (async () => {
      const { data, error } = await supabase
        .from("questionnaire_responses")
        .select("*")
        .eq("stress_event_id", stressEvent.id)
        .maybeSingle();
      if (data) {
        setSavedResponse(data);
        setBaseAnswers((data.base_answers as Record<string, string>) || {});
        setOpenAnswer(data.open_answer || "");
        setAiQuestions((data.ai_questions as Array<{ question: string; options: string[] }>) || []);
        setAiAnswers((data.ai_answers as Record<string, string>) || {});
        setInsights(data.ai_insights);
        setStep("insights");
      } else {
        setStep("base");
      }
    })();
  }, [stressEvent.id, isAnswered]);

  const handleBaseSubmit = () => {
    const allAnswered = baseQuestions.every(q => baseAnswers[q.id]);
    if (!allAnswered) {
      toast({ title: "Responda todas as perguntas", variant: "destructive" });
      return;
    }
    setStep("open");
  };

  const handleOpenSubmit = async () => {
    if (!openAnswer.trim()) {
      toast({ title: "Descreva a situação", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ai-questions", {
        body: {
          baseAnswers,
          openAnswer,
          baseQuestions: baseQuestions.map(q => ({ question: q.question, answer: baseAnswers[q.id] })),
        },
      });
      if (error) throw error;
      setAiQuestions(data.questions || []);
      setStep("ai");
    } catch (err: any) {
      toast({ title: "Erro ao gerar perguntas", description: err.message, variant: "destructive" });
    }
    setLoading(false);
  };

  const handleAiSubmit = async () => {
    const allAnswered = aiQuestions.every((_, i) => aiAnswers[`ai_${i}`]);
    if (!allAnswered) {
      toast({ title: "Responda todas as perguntas", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // Save responses
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Não autenticado");

      const { error: insertErr } = await supabase.from("questionnaire_responses").insert({
        user_id: session.user.id,
        stress_event_id: stressEvent.id,
        base_answers: baseAnswers,
        open_answer: openAnswer,
        ai_questions: aiQuestions,
        ai_answers: aiAnswers,
      });
      if (insertErr) throw insertErr;

      // Update stress event status
      await supabase.from("stress_events").update({ status: "answered" }).eq("id", stressEvent.id);

      // Generate insights
      const { data: insightsData, error: insightsErr } = await supabase.functions.invoke("generate-insights", {
        body: {
          baseAnswers,
          openAnswer,
          aiQuestions,
          aiAnswers,
          baseQuestions: baseQuestions.map(q => ({ question: q.question, answer: baseAnswers[q.id] })),
        },
      });
      if (insightsErr) throw insightsErr;

      // Save insights
      await supabase.from("questionnaire_responses")
        .update({ ai_insights: insightsData })
        .eq("stress_event_id", stressEvent.id);

      setInsights(insightsData);
      setStep("insights");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-gold" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              {step === "insights" ? "Seus Insights" : "Questionário de Estresse"}
            </h2>
          </div>
          <button onClick={step === "insights" ? onComplete : onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {step === "loading" && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-gold" />
              <p className="text-sm text-muted-foreground">Carregando avaliação...</p>
            </div>
          )}

          {step === "base" && (
            <>
              <p className="text-sm text-muted-foreground">
                Detectamos um possível pico de estresse em{" "}
                <span className="font-semibold text-foreground">
                  {new Date(stressEvent.detected_at).toLocaleString("pt-BR")}
                </span>. Responda algumas perguntas para entendermos melhor.
              </p>
              {baseQuestions.map(q => (
                <div key={q.id}>
                  <Label className="text-foreground font-medium text-sm">{q.question}</Label>
                  <RadioGroup
                    value={baseAnswers[q.id] || ""}
                    onValueChange={v => setBaseAnswers(prev => ({ ...prev, [q.id]: v }))}
                    className="mt-2 space-y-1.5"
                  >
                    {q.options.map(opt => (
                      <div key={opt} className="flex items-center gap-2">
                        <RadioGroupItem value={opt} id={`${q.id}-${opt}`} />
                        <Label htmlFor={`${q.id}-${opt}`} className="text-sm text-foreground cursor-pointer">
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
              <Button onClick={handleBaseSubmit} className="w-full bg-gold hover:bg-gold-dark text-accent-foreground">
                Continuar
              </Button>
            </>
          )}

          {step === "open" && (
            <>
              <div>
                <Label className="text-foreground font-medium">
                  Descreva com suas palavras o que aconteceu nesse momento:
                </Label>
                <Textarea
                  className="mt-2"
                  rows={5}
                  placeholder="Conte o que estava sentindo, o que aconteceu ao redor..."
                  value={openAnswer}
                  onChange={e => setOpenAnswer(e.target.value)}
                  maxLength={2000}
                />
              </div>
              <Button
                onClick={handleOpenSubmit}
                disabled={loading}
                className="w-full bg-gold hover:bg-gold-dark text-accent-foreground"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Gerando perguntas...</> : "Enviar e continuar"}
              </Button>
            </>
          )}

          {step === "ai" && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 text-gold" />
                Perguntas personalizadas geradas pela IA
              </div>
              {aiQuestions.map((q, i) => (
                <div key={i}>
                  <Label className="text-foreground font-medium text-sm">{q.question}</Label>
                  <RadioGroup
                    value={aiAnswers[`ai_${i}`] || ""}
                    onValueChange={v => setAiAnswers(prev => ({ ...prev, [`ai_${i}`]: v }))}
                    className="mt-2 space-y-1.5"
                  >
                    {q.options.map(opt => (
                      <div key={opt} className="flex items-center gap-2">
                        <RadioGroupItem value={opt} id={`ai_${i}-${opt}`} />
                        <Label htmlFor={`ai_${i}-${opt}`} className="text-sm text-foreground cursor-pointer">
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
              <Button
                onClick={handleAiSubmit}
                disabled={loading}
                className="w-full bg-gold hover:bg-gold-dark text-accent-foreground"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analisando...</> : "Finalizar e ver insights"}
              </Button>
            </>
          )}

          {step === "insights" && insights && (
            <>
              <div className="space-y-4">
                {insights.cause && (
                  <div className="bg-jade-light rounded-xl p-4">
                    <h3 className="font-display text-base font-semibold text-foreground mb-2">
                      🔍 O que pode ter causado
                    </h3>
                    <p className="text-sm text-foreground leading-relaxed">{insights.cause}</p>
                  </div>
                )}

                {insights.exercises && insights.exercises.length > 0 && (
                  <div className="bg-jade-light rounded-xl p-4">
                    <h3 className="font-display text-base font-semibold text-foreground mb-2">
                      🧘 Exercícios recomendados
                    </h3>
                    <ul className="space-y-2">
                      {insights.exercises.map((ex: string, i: number) => (
                        <li key={i} className="text-sm text-foreground flex items-start gap-2">
                          <span className="text-gold font-bold">{i + 1}.</span> {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {insights.seekHelp && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4">
                    <h3 className="font-display text-base font-semibold text-foreground mb-2">
                      ⚠️ Recomendação profissional
                    </h3>
                    <p className="text-sm text-foreground leading-relaxed">{insights.seekHelp}</p>
                  </div>
                )}
              </div>

              <Button onClick={onComplete} className="w-full bg-gold hover:bg-gold-dark text-accent-foreground">
                Fechar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StressQuestionnaire;
